
import React, { useState, useEffect } from 'react';
import './PrePublishTermsModal.css';

/**
 * مودال "قبل نشر إعلانك" - بيظهر لما المستخدم يدوس على زر نشر الإعلان
 * وبيفضل زر النشر النهائي Disabled لحد ما يوافق على الشروط عن طريق الـ checkbox.
 *
 * props:
 * - isOpen: bool
 * - onClose: function -> تقفيل المودال من غير نشر
 * - onConfirm: function -> تنفيذ عملية النشر الفعلية بعد الموافقة
 * - currentLanguage: 'ar' | 'en'
 * - getLocalizedText: function(ar, en)
 * - isSubmitting: bool -> لو النشر شغال فعليًا (تعطيل الزر + تغيير النص)
 */
function PrePublishTermsModal({
  isOpen,
  onClose,
  onConfirm,
  currentLanguage,
  getLocalizedText,
  isSubmitting
}) {
  const [agreed, setAgreed] = useState(false);
  const t = (ar, en) => getLocalizedText(ar, en);

  // Reset الموافقة كل ما المودال يتفتح من جديد
  useEffect(() => {
    if (isOpen) setAgreed(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const rules = [
    {
      title: t('الالتزام بالسعر', 'Commit to the Listed Price'),
      desc: t(
        'يجب أن يكون السعر المذكور في الإعلان هو إجمالي المبلغ الكامل الذي سيدفعه المستأجر (شامل أي رسوم للادارة إن وجدت)، ولا يجوز طلب أي رسوم إضافية بعد التواصل.',
        'The price listed in the ad must be the total rent price the tenant will pay (including any managing fees if existed), with no additional fees requested after contact.'
      )
    },
    {
      title: t('ممنوع السمسرة أو العمولات', 'No Brokerage or Commissions'),
      desc: t(
        'تُمنع أعمال السمسرة وطلب أي عمولات أو رسوم وساطة على المنصة. أي إعلان يخالف هذه السياسة سيتم حذفه، وسيتم حظر الحساب نهائيًا.',
        'Brokerage activities and requesting any commission or brokerage fees on the platform are strictly prohibited. Any listing that violates this policy will be removed, and the account will be permanently banned.'
      )
    },
    {
      title: t('عدم طلب دفعات مقدمة', 'No Upfront Payments'),
      desc: t(
        'يُمنع طلب أي عربون أو رسوم حجز أو أي تحويل مالي قبل معاينة المكان والاتفاق بين الطرفين.',
        'Requesting any deposit, booking fee, or money transfer before viewing the place and reaching an agreement is not allowed.'
      )
    },
    {
      title: t('استخدام صور حقيقية وواضحة', 'Use Real and Clear Photos'),
      desc: t(
        'يجب أن تعكس الصور حالة المكان الفعلية، وأن تكون واضحة ومرتبة وغير مضللة.',
        'Photos must reflect the actual condition of the place, and be clear, tidy, and not misleading.'
      )
    },
    {
      title: t('الالتزام بالاحترام', 'Be Respectful'),
      desc: t(
        'يجب التعامل باحترام مع جميع المستخدمين، ويُمنع أي إساءة أو سلوك غير لائق أثناء التواصل.',
        'All users must be treated with respect. Any abuse or inappropriate behavior during communication is prohibited.'
      )
    }
  ];

  return (
    <div className="prepublish-modal-overlay">
      <div
        className={`prepublish-modal ${currentLanguage === 'ar' ? 'ar' : 'en'}`}
        dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
      >
        <button
          className="prepublish-modal-close"
          onClick={onClose}
          aria-label={t('إغلاق', 'Close')}
        >
          ×
        </button>

        <div className="prepublish-modal-icon">📋</div>

        <h2 className="prepublish-modal-title">
          {t('قبل نشر إعلانك', 'Before Publishing Your Ad')}
        </h2>

        <p className="prepublish-modal-intro">
          {t(
            'للحفاظ على تجربة آمنة وواضحة للجميع، يرجى الالتزام بالقواعد التالية. مخالفة أيٍ منها قد تؤدي إلى إزالة الإعلان أو إيقاف حسابك.',
            'To keep the experience safe and clear for everyone, please commit to the following rules. Violating any of them may result in your ad being removed or your account being suspended.'
          )}
        </p>

        <ul className="prepublish-modal-rules">
          {rules.map((rule, index) => (
            <li className="prepublish-rule-item" key={index}>
              <span className="prepublish-rule-title">{rule.title}</span>
              <span className="prepublish-rule-desc">{rule.desc}</span>
            </li>
          ))}
        </ul>

        <label className="prepublish-modal-checkbox-row">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          />
          <span>
            {t(
              'أقر بأنني قرأت هذه الشروط وأوافق على الالتزام بها، وأعلم أن مخالفتها قد تؤدي إلى إزالة إعلاني أو إيقاف حسابي.',
              'I confirm that I have read these terms and agree to comply with them, and I understand that violating them may result in my ad being removed or my account being suspended.'
            )}
          </span>
        </label>

        <button
          type="button"
          className="prepublish-modal-btn"
          disabled={!agreed || isSubmitting}
          onClick={onConfirm}
        >
          {isSubmitting
            ? t('جاري النشر...', 'Publishing...')
            : t('نشر الإعلان', 'Post Ad')
          }
        </button>
      </div>
    </div>
  );
}

export default PrePublishTermsModal;